/* eslint-disable no-magic-numbers */
import { createListCollection, Box, Fieldset, Popover, Select, Text, Button } from "@chakra-ui/react";
import { ChevronLeftIconSM, ChevronRightIconSM, ChevronDownIconSM } from "@digital-services-gard-as/anchor-icons";
import { useState, useMemo, useEffect, useCallback } from "react";
import { MonthCaptionProps, useDayPicker, DateRange, DayPicker } from "react-day-picker";
import { addMonths, subDays, subMonths } from "date-fns";
import "./DateRangePicker.css";
import { useTranslation } from "react-i18next";

type DateRangePickerProps = {
    value?: DateRange;
    onChange: (range?: DateRange) => void;
};

function MonthAndYearHeader({ calendarMonth }: MonthCaptionProps) {
    const { goToMonth, dayPickerProps } = useDayPicker();

    const monthDate = calendarMonth.date;
    const monthName = monthDate.toLocaleDateString("en-GB", {
        month: "long"
    });

    const year = monthDate.getFullYear();
    const startYear = dayPickerProps.startMonth?.getFullYear() ?? year - 30;
    const endYear = dayPickerProps.endMonth?.getFullYear() ?? year + 10;
    const items = Array.from({ length: endYear - startYear + 1 }, (_, i) => {
        const y = String(startYear + i);
        return {
            label: y,
            value: y
        };
    });

    const today = new Date();

    const collection = createListCollection({
        items,
        itemToString: item => item.label,
        itemToValue: item => item.value
    });

    const handlePreviousMonth = useCallback(() => {
        goToMonth(addMonths(monthDate, -1));
    }, [goToMonth, monthDate]);

    const handleNextMonth = useCallback(() => {
        goToMonth(addMonths(monthDate, 1));
    }, [goToMonth, monthDate]);

    const handleYearChange = useCallback(
        ({ value }: { value: string[] }) => {
            const picked = value?.[0];

            if (!picked) {
                return;
            }

            const y = Number(picked);

            goToMonth(new Date(y, monthDate.getMonth(), 1));
        },
        [goToMonth, monthDate]
    );

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            paddingBlockStart="1.5rem"
            marginBottom="1rem"
        >
            <Box display="flex" alignItems="center" gap="0.375rem">
                <Button
                    aria-label="Previous month"
                    variant="plain"
                    borderRadius="a.border.radius.full"
                    paddingBlock="0.4375rem"
                    minH="2rem"
                    minW="2rem"
                    _hover={{
                        color: "a.text-default",
                        backgroundColor: "a.surface-neutral"
                    }}
                    onClick={handlePreviousMonth}
                >
                    <ChevronLeftIconSM />
                </Button>

                <Text textAlign="center" minW="70px" fontWeight={700}>
                    {monthName}
                </Text>

                {(monthDate.getFullYear() < today.getFullYear() ||
                    (monthDate.getFullYear() === today.getFullYear() && monthDate.getMonth() < today.getMonth())) && (
                    <Button
                        aria-label="Next month"
                        variant="plain"
                        borderRadius="a.border.radius.full"
                        paddingBlock="0.4375rem"
                        minH="2rem"
                        minW="2rem"
                        _hover={{
                            color: "a.text-default",
                            backgroundColor: "a.surface-neutral"
                        }}
                        onClick={handleNextMonth}
                    >
                        <ChevronRightIconSM />
                    </Button>
                )}
            </Box>

            <Select.Root
                positioning={{
                    placement: "bottom-end",
                    flip: true,
                    hideWhenDetached: true
                }}
                value={[String(year)]}
                collection={collection}
                onValueChange={handleYearChange}
                w="fit-content"
            >
                <Select.Trigger>
                    <Select.ValueText />

                    <Select.Indicator asChild>
                        <ChevronDownIconSM />
                    </Select.Indicator>
                </Select.Trigger>

                <Select.Positioner>
                    <Select.Content maxH="200px">
                        {items.map(item => (
                            <Select.Item key={item.value} item={item}>
                                <Select.ItemText>{item.label}</Select.ItemText>
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Select.Root>
        </Box>
    );
}

function isSameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function PresetDropdown({
    onPresetSelect,
    value
}: Readonly<{ onPresetSelect: (from: Date) => void; value?: DateRange }>) {
    const { t } = useTranslation();

    const today = new Date();

    const presets = [
        { label: t("common:dateRange.today"), from: today },
        {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            label: t("common:dateRange.last14Days"),
            from: subDays(today, 13)
        },
        {
            label: t("common:dateRange.last30Days"),
            from: subDays(today, 29)
        },
        {
            label: t("common:dateRange.last3Months"),
            from: subMonths(today, 3)
        },
        {
            label: t("common:dateRange.last6Months"),
            from: subMonths(today, 6)
        },
        {
            label: t("common:dateRange.last12Months"),
            from: subMonths(today, 12)
        }
    ];

    const collection = createListCollection({
        items: presets,
        itemToString: item => item.label,
        itemToValue: item => item.label
    });

    const selectedLabel = useMemo(() => {
        if (!value?.from || !value?.to) {
            return undefined;
        }

        if (!isSameDay(value.to, today)) {
            return undefined;
        }

        return presets.find(p => value.from && isSameDay(p.from, value.from))?.label;
    }, [value, today, presets]);

    const handlePresetValueChange = useCallback(
        ({ value: selectedValues }: { value: string[] }) => {
            const selectedPreset = presets.find(preset => preset.label === selectedValues?.[0]);

            if (selectedPreset) {
                onPresetSelect(selectedPreset.from);
            }
        },
        [onPresetSelect, presets]
    );

    return (
        <Box>
            <Select.Root
                positioning={{
                    placement: "bottom-start",
                    flip: true,
                    hideWhenDetached: true
                }}
                value={selectedLabel ? [selectedLabel] : []}
                collection={collection}
                onValueChange={handlePresetValueChange}
            >
                <Select.Trigger className="date-range-preset-trigger">
                    <Select.ValueText
                        placeholder={value?.from ? t("common:dateRange.custom") : t("common:dateRange.last14Days")}
                    />

                    <Select.Indicator asChild>
                        <ChevronDownIconSM />
                    </Select.Indicator>
                </Select.Trigger>

                <Select.Positioner>
                    <Select.Content className="date-range-preset-content">
                        {presets.map(preset => (
                            <Select.Item key={preset.label} item={preset} className="date-range-preset-item">
                                <Select.ItemText>{preset.label}</Select.ItemText>
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Select.Root>
        </Box>
    );
}

export const DateRangePicker = ({ value, onChange }: DateRangePickerProps) => {
    const { t } = useTranslation();

    const today = new Date();

    const nextMonth = addMonths(today, 1);

    const [open, setOpen] = useState(false);

    const [startMonth] = useState(new Date(1990, 0, 1));

    const [endMonth] = useState(nextMonth);

    const [month, setMonth] = useState<Date>(today);

    useEffect(() => {
        if (value?.from) {
            setMonth(value.from);
        }
    }, [value?.from]);

    const fmt = (date?: Date) =>
        date
            ? date.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric"
              })
            : "";

    const presets = [
        { label: t("common:dateRange.today"), from: today },
        { label: t("common:dateRange.last14Days"), from: subDays(today, 13) },
        { label: t("common:dateRange.last30Days"), from: subDays(today, 29) },
        { label: t("common:dateRange.last3Months"), from: subMonths(today, 3) },
        { label: t("common:dateRange.last6Months"), from: subMonths(today, 6) },
        { label: t("common:dateRange.last12Months"), from: subMonths(today, 12) }
    ];

    const label = useMemo(() => {
        if (value?.from && value?.to) {
            if (isSameDay(value.to, today)) {
                const matchedPreset = presets.find(p => value.from && isSameDay(p.from, value.from));
                if (matchedPreset) {
                    return matchedPreset.label;
                }
            }

            return `${fmt(value.from)} - ${fmt(value.to)}`;
        }

        if (value?.from) {
            return `${fmt(value.from)} -`;
        }

        return "Select";
    }, [value]);

    const applyRange = useCallback(
        (from: Date) => {
            onChange({
                from,
                to: today
            });

            setMonth(from);
        },
        [onChange, today]
    );

    const handleOpenChange = useCallback((details: { open: boolean }) => {
        setOpen(details.open);
    }, []);

    return (
        <Fieldset.Root border="none" p="0" m="0">
            <Popover.Root
                portalled
                open={open}
                onOpenChange={handleOpenChange}
                unmountOnExit
                positioning={{
                    placement: "bottom",
                    gutter: 0,
                    strategy: "fixed",
                    hideWhenDetached: true,
                    offset: {
                        crossAxis: 0,
                        mainAxis: -30
                    }
                }}
            >
                <Popover.Trigger asChild>
                    <Button
                        data-testid="dateRangePicker"
                        size="lg"
                        variant="outline"
                        justifyContent="space-between"
                        maxWidth="20rem"
                    >
                        {label}

                        <ChevronDownIconSM
                            style={{
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s linear"
                            }}
                        />
                    </Button>
                </Popover.Trigger>

                <Popover.Positioner>
                    <Popover.Content
                        p="1.5rem"
                        boxShadow="a.shadow.xlarge"
                        overflow="hidden"
                        minW="340px"
                        borderRadius="8px"
                        bg="white"
                    >
                        <PresetDropdown onPresetSelect={applyRange} value={value} />
                        <Box>
                            <DayPicker
                                showOutsideDays
                                disabled={{
                                    after: new Date()
                                }}
                                mode="range"
                                selected={value}
                                onSelect={onChange}
                                numberOfMonths={1}
                                weekStartsOn={0}
                                startMonth={startMonth}
                                endMonth={endMonth}
                                month={month}
                                fixedWeeks
                                onMonthChange={setMonth}
                                hideNavigation
                                components={{
                                    MonthCaption: MonthAndYearHeader
                                }}
                                defaultMonth={today}
                                formatters={{
                                    formatWeekdayName(weekday) {
                                        return weekday.toLocaleDateString("en-GB", {
                                            weekday: "short"
                                        });
                                    }
                                }}
                            />
                        </Box>
                    </Popover.Content>
                </Popover.Positioner>
            </Popover.Root>
        </Fieldset.Root>
    );
};
